import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "../Base/Dialog";
import { Loading } from "../Base";
import {
  batchBonusApi,
  BATCH_BONUS_UPDATED_EVENT,
  BATCH_BONUS_CLAIM_PATH,
} from "../../api/batchBonusApi";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { useT } from "../../i18n/hooks";
import { useCurrencySymbol } from "../../hooks/useUtils";
import utils from "../../utils";
import { useNotificationStore } from "../../modules/notification/stores";
import { useUserStore } from "../../modules/user/stores";
import "./BatchBonusClaimDialog.scss";

const formatDate = (v: string | number | null | undefined): string => {
  if (v == null || v === "") return "";
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return String(v);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

interface ClaimResultState {
  status: string;
  amount: number;
  iconUrl: string;
  endTime?: string | number | null;
}

/**
 * 批量赠金领取网关（F31）。全局常驻；仅当路径为 BATCH_BONUS_CLAIM_PATH?id=<id>
 * 时触发一次领取逻辑，用项目统一的 Base/Dialog 展示结果（成功 / 已领取 / 已过期），
 * 样式与站内信弹窗一致、全走主题；点击 Confirm 关闭并返回首页。
 */
export const BatchBonusClaimGate: React.FC = () => {
  const { pathname, search } = useLocation();
  const { navigate } = useNavigation();
  const t = useT();
  const symbol = useCurrencySymbol();
  const processedRef = useRef<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<ClaimResultState | null>(null);

  useEffect(() => {
    // 离开领取页：清幂等锁与本地态。否则中途返回（返回键再进同一 id）会因
    // processedRef 仍锁住、result 又为空，出现空白无弹窗。
    if (pathname !== BATCH_BONUS_CLAIM_PATH) {
      processedRef.current = null;
      setClaiming(false);
      setResult(null);
      return;
    }
    const id = new URLSearchParams(search).get("id");
    if (!id) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }
    // 幂等：同一 id 只处理一次（避免请求进行中重复触发）；关闭弹窗/离开页时重置以允许重试
    if (processedRef.current === id) {
      return;
    }
    processedRef.current = id;

    void (async () => {
      setClaiming(true);
      setResult(null);
      let next: ClaimResultState = { status: "FAILED", amount: 0, iconUrl: "" };
      try {
        const resp = (await batchBonusApi.claim(id)) as any;
        const model = resp?.data?.model ?? resp?.data ?? {};
        // 图标复用该通知类型的 icon：优先用站内信统计存入 store 的原始 resourceId；
        // store 为空时（如直接打开领取链接）用后端返回的通知类型图标兜底
        const iconRaw =
          useNotificationStore.getState().batchBonusIcon || model.icon;
        next = {
          status: model.status || "FAILED",
          amount: Number(model.amount) || 0,
          iconUrl: iconRaw ? utils.getImageUrl(iconRaw) : "",
          endTime: model.endTime,
        };
      } catch {
        next = { status: "FAILED", amount: 0, iconUrl: "" };
      }
      // 领取成功是既成事实：刷新角标/列表；失败/已领取/已过期不触发无谓刷新
      if (next.status === "OK") {
        window.dispatchEvent(new Event(BATCH_BONUS_UPDATED_EVENT));
        // 赠金已入余额，刷新全局余额（否则钱包不更新）
        useUserStore.getState().loadUserRealtime();
      }
      // 仅当 URL 仍是本次 id：结束 loading / 写结果，避免旧请求关掉新请求的 loading 或覆盖结果
      if (new URLSearchParams(window.location.search).get("id") === id) {
        setClaiming(false);
        setResult(next);
      }
    })();
    // 不用 cleanup 取消 setResult：effect 可能因 navigate 引用变化重跑，取消会误伤正常领取
    // 导致结果弹窗不显示。离开领取页由上方重置 + 下方 pathname 守卫兜住。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  const handleClose = () => {
    // 重置 processedRef：关闭后允许对同一 id 再次处理（领取失败可重试；后端幂等保证不会重复发放）
    processedRef.current = null;
    setResult(null);
    navigate(ROUTES.HOME, { replace: true });
  };

  // 已离开领取路径：不展示任何弹窗（双保险，异步完成后避免弹到首页等其它页面）
  if (pathname !== BATCH_BONUS_CLAIM_PATH) {
    return null;
  }
  // 领取请求进行中：显示 Loading，避免 API 返回前的空白页
  if (claiming) {
    return (
      <Dialog
        visible
        size="sm"
        className="batch-bonus-claim-dialog"
        getContainer={() => document.body}
      >
        <div className="batch-bonus-claim-dialog__body">
          <Loading variant="app" />
        </div>
      </Dialog>
    );
  }

  if (!result) {
    return null;
  }

  const isSuccess = result.status === "OK";
  let message: string;
  if (result.status === "OK") {
    message = t("batchBonus.successMessage");
  } else if (result.status === "EXPIRED") {
    message = t("batchBonus.expiredMessage", { date: formatDate(result.endTime) });
  } else if (result.status === "ALREADY_CLAIMED") {
    message = t("batchBonus.alreadyClaimedMessage");
  } else {
    message = t("batchBonus.failed");
  }

  return (
    <Dialog
      visible
      size="sm"
      className="batch-bonus-claim-dialog"
      confirmText={t("common.confirm")}
      onConfirm={handleClose}
      onClose={handleClose}
      getContainer={() => document.body}
    >
      <div className="batch-bonus-claim-dialog__body">
        {isSuccess && result.iconUrl ? (
          <div className="batch-bonus-claim-dialog__icon-wrap">
            <img
              className="batch-bonus-claim-dialog__icon"
              src={result.iconUrl}
              alt=""
            />
          </div>
        ) : null}
        {isSuccess ? (
          <h3 className="batch-bonus-claim-dialog__title">
            {t("batchBonus.successTitle")}
          </h3>
        ) : null}
        <div className="batch-bonus-claim-dialog__content">{message}</div>
        {isSuccess && result.amount > 0 ? (
          <div className="batch-bonus-claim-dialog__amount">
            +{symbol}
            {result.amount.toFixed(2)}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export default BatchBonusClaimGate;
